/**
 * My Devices Page
 *
 * Shows the current user's registered devices.
 * Users can view their devices but cannot approve or manage them.
 */

import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  PhoneAndroid as DeviceIcon,
  Computer as DesktopIcon,
  Tablet as TabletIcon,
  CheckCircle as ActiveIcon,
  HourglassEmpty as PendingIcon,
  Cancel as InactiveIcon,
  Warning as RevokedIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { devicesApi } from '../../services/devicesApi';
import { useDeviceAuth } from '../../contexts/DeviceAuthContext';

type DeviceStatus = 'pending' | 'active' | 'inactive' | 'revoked';

const statusConfig: Record<DeviceStatus, { color: 'warning' | 'success' | 'default' | 'error'; icon: React.ReactNode; label: string }> = {
  pending: { color: 'warning', icon: <PendingIcon />, label: 'Pending Approval' },
  active: { color: 'success', icon: <ActiveIcon />, label: 'Active' },
  inactive: { color: 'default', icon: <InactiveIcon />, label: 'Inactive' },
  revoked: { color: 'error', icon: <RevokedIcon />, label: 'Revoked' },
};

function getDeviceIcon(deviceName: string) {
  const name = deviceName.toLowerCase();
  if (name.includes('phone') || name.includes('iphone') || name.includes('android phone')) {
    return <DeviceIcon />;
  }
  if (name.includes('tablet') || name.includes('ipad')) {
    return <TabletIcon />;
  }
  return <DesktopIcon />;
}

export function MyDevicesPage() {
  const { deviceToken } = useDeviceAuth();

  const { data: devices, isLoading, error } = useQuery({
    queryKey: ['my-devices'],
    queryFn: () => devicesApi.getMyDevices(),
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load your devices. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <DeviceIcon fontSize="large" color="primary" />
        <Typography variant="h4">My Devices</Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        These are the devices you have registered to access your account. New devices require
        manager approval before you can log in from them.
      </Alert>

      {devices?.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <DeviceIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No devices registered
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When you log in from a new device, it will appear here.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {devices?.map((device) => {
            const isCurrentDevice = device.deviceToken === deviceToken;

            return (
              <Card key={device._id} sx={{ mb: 2 }}>
                <ListItem>
                  <ListItemIcon>{getDeviceIcon(device.deviceName)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {device.deviceName}
                        </Typography>
                        {isCurrentDevice && (
                          <Chip label="This Device" size="small" color="primary" variant="outlined" />
                        )}
                        <Chip
                          icon={statusConfig[device.status].icon as React.ReactElement}
                          label={statusConfig[device.status].label}
                          color={statusConfig[device.status].color}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block' }}>
                        <Typography variant="body2" color="text.secondary" component="span">
                          {device.browser} / {device.os}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                <Divider />
                <CardContent>
                  <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Registered
                      </Typography>
                      <Typography variant="body2">
                        {format(new Date(device.createdAt), 'PPP')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Last Used
                      </Typography>
                      <Typography variant="body2">
                        {formatDistanceToNow(new Date(device.lastUsedAt), { addSuffix: true })}
                      </Typography>
                    </Box>
                    {device.approvedBy && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Approved By
                        </Typography>
                        <Typography variant="body2">
                          {typeof device.approvedBy === 'object' ? device.approvedBy.name : 'Manager'}
                        </Typography>
                      </Box>
                    )}
                    {device.expiresAt && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Expires
                        </Typography>
                        <Typography variant="body2">
                          {format(new Date(device.expiresAt), 'PPP')}
                        </Typography>
                      </Box>
                    )}
                    {device.deactivationReason && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {device.status === 'revoked' ? 'Revocation Reason' : 'Deactivation Reason'}
                        </Typography>
                        <Typography variant="body2">{device.deactivationReason}</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </List>
      )}
    </Box>
  );
}
